
import { computed, isRef } from '@nuxtjs/composition-api';
import useProductType from '~/composable/useProductType';
import useTlmCart from '~/composable/useTlmCart';
import { CART_ITEM_GROUP_PURCHASE_TYPE } from '~/constants/CheckoutFlow';


// eslint-disable-next-line complexity
export default function useCartQueries (appContext, cart, options = {}) {
  const { skipAutoLoad = false } = options
  const { isSubscription, isMobileBroadBandSubscription, isMobileVoiceSubscription } = useProductType()
  const hubbleClient = appContext.$hubble;
  const { cart: loadedCart, loadOrCreateCart, containsKeepSubscription } = useTlmCart(appContext);
  const loadCart = async () => {
    await loadOrCreateCart();
    return loadedCart;
  }

  if (!appContext || !hubbleClient) {
    throw new Error('You must provide the nuxt app context to use this composable');
  }
  if (!skipAutoLoad && (!isRef(cart) || !cart.value || Object.keys(cart.value).length < 1)) {
    cart = loadCart();
  }
  const isSignUpPurchaseType = (cartItemGroupPurchaseType) => {
    return cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_CASH_WITH_NEW_SUBSCRIPTION ||
      cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_CASH_WITH_NP_SUBSCRIPTION ||
      cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_INSTALLMENT_WITH_NP_SUBSCRIPTION ||
      cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_INSTALLMENT_WITH_NEW_SUBSCRIPTION
  }
  const isChangeSubscriptionPurchaseType = (cartItemGroupPurchaseType) => {
    return cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_CASH_WITH_CHANGE_SUBSCRIPTION ||
      cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_INSTALLMENT_WITH_CHANGE_SUBSCRIPTION
  }
  const isKeepSubscriptionPurchaseType = (cartItemGroupPurchaseType) => {
    return cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_CASH_WITH_KEEPING_SUBSCRIPTION ||
      cartItemGroupPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_INSTALLMENT_WITH_KEEPING_SUBSCRIPTION
  }
  const getDeviceWithInstallment = computed(() => {
    const devices = []
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const device = cartItemGroup.cartItems.find(cartItem => !isSubscription(cartItem.cartProduct.productTypeKey) &&
        cartItem.installmentPeriod > 0)
      if (device !== undefined) {
        return devices.push(device)
      }
    })
    // when BE can handle multiple devices, we will send it as an array of values
    return devices.length > 0 ? devices[0] : []
  })
  const playMixProducts = computed(() => {
    const playMixItems = []
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const playMixSubscription = cartItemGroup.cartItems.find(cartItem => isSubscription(cartItem.cartProduct.productTypeKey) && cartItem.cartProduct.additionalBillingInformation.isMixProduct)
      if (playMixSubscription !== undefined) {
        return playMixItems.push(playMixSubscription)
      }
    })
    // when BE can handle multiple devices, we will send it as an array of values
    return playMixItems[0]
  })
  const cartOnlyContainsSubscription = computed(() => { // (sim-only)
    let subscriptionOnly = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const notSubscription = cartItemGroup.cartItems.find(cartItem => !isSubscription(cartItem.cartProduct.productTypeKey))
      if (!notSubscription) {
        subscriptionOnly = true
      }
    })
    return subscriptionOnly
  })
  const isAnyItemAssociatedWithMobileVoiceSubscription = computed(() => {
    let isAnyItemAssociatedWithMobileVoiceSubscription = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const carteItemWiThSubscription = cartItemGroup.cartItems.find(cartItem => isMobileVoiceSubscription(cartItem.cartProduct.productTypeKey))
      if (carteItemWiThSubscription !== undefined) {
        isAnyItemAssociatedWithMobileVoiceSubscription = true;
      }
    })
    return isAnyItemAssociatedWithMobileVoiceSubscription;
  })
  const isAnyItemAssociatedWithMobileBroadBand = computed(() => {
    let isAnyItemAssociatedWithMobileBroadBand = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const carteItemWiThSubscription = cartItemGroup.cartItems.find(cartItem => isMobileBroadBandSubscription(cartItem.cartProduct.productTypeKey))
      if (carteItemWiThSubscription !== undefined) {
        isAnyItemAssociatedWithMobileBroadBand = true;
      }
    })
    return isAnyItemAssociatedWithMobileBroadBand;
  })
  const isAnyItemAssociatedWithSubscription = computed(() => {
    let isAnyItemAssociatedWithSubscription = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const carteItemWiThSubscription = cartItemGroup.cartItems?.find(
        cartItem => cartItem?.cartProduct && (
          isMobileVoiceSubscription(cartItem.cartProduct.productTypeKey) ||
          isMobileBroadBandSubscription(cartItem.cartProduct.productTypeKey)
        )
      )
      if (carteItemWiThSubscription !== undefined) {
        isAnyItemAssociatedWithSubscription = true;
      }
    })
    return isAnyItemAssociatedWithSubscription;
  })
  const hasValidAttributeValue = (subscriptionItem, attributeName) => {
    if (!subscriptionItem?.itemAttributes) {
      return false
    }
    const attribute = subscriptionItem.itemAttributes.find(attr => attr.name === attributeName)
    const value = attribute?.value
    return value !== undefined && value !== null && value !== ''
  }

  const validPhoneNumber = (subscriptionItem) => {
    return hasValidAttributeValue(subscriptionItem, 'PhoneNumber')
  }

  const hasValidPrepaidAccountIdAttr = (subscriptionItem) => {
    return hasValidAttributeValue(subscriptionItem, 'PrepaidAccountId')
  }

  const hasValidKeepSubscriptionData = (subscriptionItem, cartItemGrpPurchaseType) => {
    if (!subscriptionItem) {
      return false
    }
    if (hasValidPrepaidAccountIdAttr(subscriptionItem) || hasValidAttributeValue(subscriptionItem, 'ServiceAccountId')) {
      return true
    }
    if (isKeepSubscriptionPurchaseType(cartItemGrpPurchaseType)) {
      return true
    }
    return containsKeepSubscription.value === true
  }

  const isKeepSubscriptionScenario = (cartItemGrpPurchaseType) => {
    if (containsKeepSubscription.value === true) {
      return true
    }
    return isKeepSubscriptionPurchaseType(cartItemGrpPurchaseType)
  }

  const hasValidDataForPurchaseScenario = computed(() => {
    let hasValidData = false
    // eslint-disable-next-line complexity
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const cartItemGrpPurchaseType = cartItemGroup.cartItemGroupPurchaseType
      const subscriptionItem = cartItemGroup.cartItems.find(item => isSubscription(item.cartProduct.productTypeKey))
      if (isSignUpPurchaseType(cartItemGrpPurchaseType)) {
        hasValidData = validPhoneNumber(subscriptionItem)
      } else if (isKeepSubscriptionScenario(cartItemGrpPurchaseType)) {
        hasValidData = hasValidKeepSubscriptionData(subscriptionItem, cartItemGrpPurchaseType)
      } else if (isChangeSubscriptionPurchaseType(cartItemGrpPurchaseType)) {
        hasValidData = hasValidPrepaidAccountIdAttr(subscriptionItem)
      } else if (cartItemGrpPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.SUBSCRIPTION_ONLY) {
        hasValidData = validPhoneNumber(subscriptionItem) || hasValidPrepaidAccountIdAttr(subscriptionItem)
      } else if (cartItemGrpPurchaseType === CART_ITEM_GROUP_PURCHASE_TYPE.HARDWARE_CASH_WITHOUT_SUBSCRIPTION) {
        hasValidData = cartItemGroup.cartItems.length === 1 && subscriptionItem === undefined
      }
    })
    return hasValidData
  })
  const cartItemGroupPurchaseType = computed(() => {
    const cartItemGroupPurchaseType = []
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      cartItemGroupPurchaseType.push(cartItemGroup.cartItemGroupPurchaseType)
    })
    return cartItemGroupPurchaseType
  })
  const hasAnyInstallments = computed(() => {
    const installments = []
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const device = cartItemGroup.cartItems.find(cartItem => !isSubscription(cartItem.cartProduct.productTypeKey))
      if (device !== undefined && device.installmentPeriod > 0) {
        return installments.push(device.installmentPeriod)
      }
    })
    // when BE can handle multiple installments, we will send it as an array of values
    return installments.length > 0
  })
  return {
    hasAnyInstallments,
    cartItemGroupPurchaseType,
    isKeepSubscriptionPurchaseType,
    hasValidDataForPurchaseScenario,
    getDeviceWithInstallment,
    playMixProducts,
    cartOnlyContainsSubscription,
    isAnyItemAssociatedWithMobileVoiceSubscription,
    isAnyItemAssociatedWithMobileBroadBand,
    isAnyItemAssociatedWithSubscription
  }
}
